import { isFilterId, type MemberFilters } from "#shared/reviewee-filters/memberFilters.ts";
import { TENURE_BUCKET_KEYS, toTenureFilters } from "#shared/reviewee-filters/tenureBuckets.ts";
import { toIsReportingManager, TrackOptions } from "#shared/reviewee-filters/trackOptions.ts";
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";
import type { FilterKind } from "./filterKinds.ts";

export const revieweeFilterQuerySchema = {
    managerIds: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    segmentIds: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    reportingLineIds: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    departments: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    genders: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    workLocations: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    jobLevels: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    jobTitles: parseAsArrayOf(parseAsString).withDefault([] as string[]),
    tenure: parseAsStringLiteral(TENURE_BUCKET_KEYS),
    track: parseAsStringLiteral(TrackOptions)
};

export const REVIEWEE_FILTER_QUERY_KEYS = Object.keys(revieweeFilterQuerySchema);

const CLEARED_FILTERS = Object.fromEntries(REVIEWEE_FILTER_QUERY_KEYS.map(key => [key, null]));

const FILTER_QUERY_RESET: Record<FilterKind, Record<string, null>> = {
    directManagers: { managerIds: null },
    reportingLine: { reportingLineIds: null },
    segment: { segmentIds: null },
    department: { departments: null },
    gender: { genders: null },
    workLocation: { workLocations: null },
    roleLevel: { jobLevels: null },
    jobTitle: { jobTitles: null },
    tenure: { tenure: null },
    track: { track: null }
};

const queryStateOptions = { history: "replace" as const, clearOnDefault: true, shallow: false };

export function useRevieweeFilters() {
    const [{ managerIds: managerIdsParam, segmentIds: segmentIdsParam, reportingLineIds: reportingLineIdsParam, departments, genders, workLocations, jobLevels, jobTitles, tenure, track }, setFilters] = useQueryStates(revieweeFilterQuerySchema, queryStateOptions);

    const managerIds = useMemo(() => managerIdsParam.filter(isFilterId), [managerIdsParam]);
    const segmentIds = useMemo(() => segmentIdsParam.filter(isFilterId), [segmentIdsParam]);
    const reportingLineIds = useMemo(() => reportingLineIdsParam.filter(isFilterId), [reportingLineIdsParam]);

    const memberFilters = useMemo<MemberFilters>(() => {
        const tenureFilters = tenure ? toTenureFilters(tenure, new Date()) : undefined;
        const isReportingManager = toIsReportingManager(track);

        return {
            ...(managerIds.length > 0 && { managerIds }),
            ...(segmentIds.length > 0 && { segmentIds }),
            ...(reportingLineIds.length > 0 && { reportingLineIds }),
            ...(departments.length > 0 && { departments }),
            ...(genders.length > 0 && { genders }),
            ...(workLocations.length > 0 && { workLocations }),
            ...(jobLevels.length > 0 && { jobLevels }),
            ...(jobTitles.length > 0 && { jobTitles }),
            ...(tenureFilters?.hireDateMin && { hireDateMin: tenureFilters.hireDateMin }),
            ...(tenureFilters?.hireDateMax && { hireDateMax: tenureFilters.hireDateMax }),
            ...(isReportingManager !== undefined && { isReportingManager })
        };
    }, [managerIds, segmentIds, reportingLineIds, departments, genders, workLocations, jobLevels, jobTitles, tenure, track]);

    const clearFilters = useCallback(() => void setFilters(CLEARED_FILTERS), [setFilters]);

    const removeFilter = useCallback((kind: FilterKind) => void setFilters(FILTER_QUERY_RESET[kind]), [setFilters]);

    const hasActiveFilters = Object.keys(memberFilters).length > 0;

    return { managerIds, segmentIds, reportingLineIds, departments, genders, workLocations, jobLevels, jobTitles, tenure, track, memberFilters, clearFilters, removeFilter, hasActiveFilters, setFilters };
}
